import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Wallet, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN = 'Daquan22';
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const shiftWeek = (iso, days) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const PayoutRow = ({ r, weekStart, onChange }) => {
  const paid = r.payout;
  const toggle = async () => {
    try {
      await axios.post(`${API}/payouts/mark-paid`, {
        admin_secret: ADMIN, engineer_name: r.engineer_name, week_start: weekStart, amount: paid ? paid.amount : r.engineer_pay, undo: !!paid,
      });
      toast.success(paid ? 'Payout un-marked' : `${r.engineer_name} marked paid ${money(r.engineer_pay)}`);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };
  return (
    <tr data-testid={`payout-row-${r.engineer_name}`} className="border-t border-neutral-800">
      <td className="py-3 pr-3 text-white font-semibold">{r.engineer_name}</td>
      <td className="py-3 pr-3 text-neutral-400">{r.sessions} · {r.hours}h</td>
      <td className="py-3 pr-3 text-right text-neutral-200">{money(r.collected)}</td>
      <td className="py-3 pr-3 text-right text-neutral-500">-{money(r.stripe_fees)}</td>
      <td className="py-3 pr-3 text-right text-neutral-200">{money(r.net)}</td>
      <td className="py-3 pr-3 text-right text-purple-300 font-bold" data-testid={`payout-amount-${r.engineer_name}`}>{money(r.engineer_pay)}</td>
      <td className="py-3 pr-3 text-right text-neutral-400">{money(r.studio_keep)}</td>
      <td className="py-3 text-right">
        <button
          onClick={toggle}
          disabled={!paid && r.engineer_pay <= 0}
          data-testid={`payout-mark-${r.engineer_name}`}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 disabled:opacity-30 ${paid ? 'bg-green-500/15 text-green-300 border border-green-500/30' : 'bg-purple-600 text-black hover:bg-purple-500'}`}
        >
          {paid ? <span className="inline-flex items-center gap-1"><Check size={12} /> Paid {money(paid.amount)}</span> : 'Mark paid'}
        </button>
      </td>
    </tr>
  );
};

export const PayoutSheet = () => {
  const [week, setWeek] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/payouts`, { params: { admin_secret: ADMIN, week_start: week || undefined } })
      .then((r) => { setData(r.data); if (!week) setWeek(r.data.week_start); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load payouts'));
  }, [week]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="payout-sheet" className="bg-black/40 border border-neutral-800 rounded-2xl p-5 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Wallet size={20} className="text-purple-400" />
          <h3 className="text-white font-bold text-lg">Engineer payouts</h3>
          <span className="text-xs text-neutral-500">30% engineer · 70% studio · after Stripe fees</span>
        </div>
        {data && (
          <div className="flex items-center gap-2">
            <button data-testid="payout-prev-week" onClick={() => setWeek(shiftWeek(data.week_start, -7))} className="p-1.5 rounded-full border border-neutral-700 text-neutral-300 hover:bg-neutral-800"><ChevronLeft size={16} /></button>
            <span className="text-sm text-neutral-300" data-testid="payout-week-label">{data.week_start} → {data.week_end}</span>
            <button data-testid="payout-next-week" onClick={() => setWeek(shiftWeek(data.week_start, 7))} className="p-1.5 rounded-full border border-neutral-700 text-neutral-300 hover:bg-neutral-800"><ChevronRight size={16} /></button>
          </div>
        )}
      </div>
      <p className="text-neutral-500 text-xs mb-4">By session date. Collected = deposits + balances paid. Open studio balances aren't included until they're marked paid.</p>
      {!data ? null : data.engineers.length === 0 ? (
        <p className="text-neutral-400 text-sm" data-testid="payout-empty">No sessions this week.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-neutral-500 text-left">
                <th className="pb-2 pr-3">Engineer</th><th className="pb-2 pr-3">Sessions</th>
                <th className="pb-2 pr-3 text-right">Collected</th><th className="pb-2 pr-3 text-right">Stripe fees</th>
                <th className="pb-2 pr-3 text-right">Net</th><th className="pb-2 pr-3 text-right">Engineer 30%</th>
                <th className="pb-2 pr-3 text-right">Studio 70%</th><th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {data.engineers.map((r) => <PayoutRow key={r.engineer_name} r={r} weekStart={data.week_start} onChange={load} />)}
              <tr className="border-t border-neutral-700 font-bold" data-testid="payout-totals">
                <td className="py-3 text-white">Total</td><td />
                <td className="py-3 pr-3 text-right text-white">{money(data.totals.collected)}</td>
                <td className="py-3 pr-3 text-right text-neutral-500">-{money(data.totals.stripe_fees)}</td>
                <td className="py-3 pr-3 text-right text-white">{money(data.totals.net)}</td>
                <td className="py-3 pr-3 text-right text-purple-300">{money(data.totals.engineer_pay)}</td>
                <td className="py-3 pr-3 text-right text-white">{money(data.totals.studio_keep)}</td><td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
